/**
 * Registration — approved 30 September 2026 (Q1, Q6).
 *
 * One checkbox and an address. No "trial", no countdown, no second
 * confirmation step, and nothing emailed to finish it: Q6 is explicit that
 * the visitor registers and continues directly in the workspace.
 *
 * The checkbox is deliberately NOT followed by "I can unsubscribe at any
 * time" — Or ruled that out, and the unsubscribe lives in the emails where
 * someone reading one can act on it.
 */

import React, { useEffect, useRef, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { copyFor, SHARED } from './audienceCopy';

const isEmail = (v) => /^[^@\s,;]+@[^@.\s,;]+(\.[^@.\s,;]+)+$/.test(String(v || '').trim());

export default function EnrollStep({ audience, onEnroll, submitting, error }) {
  const copy = copyFor(audience);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const consentRef = useRef(null);

  // The caret waits in the first field, see MockEnroll for why.
  useEffect(() => { nameRef.current?.focus(); }, []);

  // What is still missing, or null when the form is ready. See MockEnroll.
  const missing = isEmail(email)
    ? (consent ? null : SHARED.consentRequired)
    : (consent ? SHARED.emailRequired : SHARED.emailAndConsentRequired);

  const submit = () => {
    setTouched(true);
    if (submitting) return;
    if (missing) {
      if (!consent) consentRef.current?.focus();
      else emailRef.current?.focus();
      return;
    }
    onEnroll({ email: email.trim(), name: name.trim(), consent: true });
  };

  return (
    <aside className="cmp-enroll">
      <h2 className="cmp-section-title">{copy.invitation}</h2>

      <div className="cmp-field">
        <label className="cmp-label" htmlFor="cmp-enroll-name">
          Your name <span className="cmp-optional">Optional</span>
        </label>
        {/* Only ever used to greet them in the curated email, and the
            greeting is omitted when it is absent rather than guessed. */}
        <input
          id="cmp-enroll-name"
          ref={nameRef}
          className="cmp-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="cmp-field">
        <label className="cmp-label" htmlFor="cmp-enroll-email">{SHARED.emailLabel}</label>
        <input
          id="cmp-enroll-email"
          ref={emailRef}
          className="cmp-input"
          type="email"
          value={email}
          placeholder={SHARED.emailPlaceholder}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <label className="cmp-choice">
        <input
          type="checkbox"
          ref={consentRef}
          checked={consent}
          onChange={() => setConsent((c) => !c)}
        />
        <span>{copy.registrationCheckbox}</span>
      </label>

      {touched && missing && <div className="cmp-error">{missing}</div>}
      {error && <div className="cmp-error">{error}</div>}

      <div className="cmp-actions">
        {/* `aria-disabled`, not `disabled`, see MockEnroll. */}
        <button
          type="button"
          className={`cmp-btn cmp-btn--primary${missing ? ' cmp-btn--inactive' : ''}`}
          disabled={submitting}
          aria-disabled={missing ? 'true' : undefined}
          onClick={submit}
        >
          {SHARED.getMyOpportunities}
          <ArrowForwardIcon style={{ fontSize: 16 }} />
        </button>
      </div>
    </aside>
  );
}
